import React, { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Link from '../../components/LanguageSwitcher/LocaleLink'
import { motion } from 'framer-motion'
import Layout from '../../components/layout/layout'
import Reveal from '../../components/Reveal/Reveal'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import Seo, { breadcrumbJsonLd, serviceDetailJsonLd, faqJsonLd } from '../../components/Seo/Seo'
import useEntryMotion from '../../components/motion/useEntryMotion'
import AbstractVisual from '../../components/AbstractVisual/AbstractVisual'
import { serviceCategoryPhotos } from '../../data/photos'
import { resolveProjects } from '../../data/projects'
import ProjectMedia from '../../components/ProjectMedia/ProjectMedia'
import { getService, SERVICE_CATEGORIES } from '../../data/services'
import { localizeService } from '../../data/localizeService'
import track from '../../lib/track'
import QuoteForm from '../../components/QuoteForm/QuoteForm'
import RelatedArticles from '../../components/RelatedArticles/RelatedArticles'
import { SERVICE_ARTICLES } from '../../data/topicArticles'

import './services.scss'

// Which estimator project type a service page hands off to.
const ESTIMATOR_TYPE = {
  'ai-development': 'ml', 'generative-ai': 'genai', rag: 'rag', 'ai-agents': 'agents',
  'computer-vision': 'cv', 'nlp-chatbots': 'chatbot', 'data-engineering': 'data', mlops: 'ml',
  'web-development': 'web', 'mobile-development': 'mobile', 'ui-ux-design': 'web', 'ai-consulting': 'ml',
  'georgian-language-ai': 'chatbot', 'nearshore-ai-development-europe': 'ml', seo: 'web',
}

// Which option of the enquiry form's "What do you need?" list fits each type.
const NEED_INDEX = { ml: 0, cv: 1, genai: 2, rag: 2, chatbot: 2, data: 3, web: 4, mobile: 4, agents: 5 }

const pad = (n) => String(n).padStart(2, '0')

/**
 * One page per service, rendered from data/services.js. The section order
 * mirrors a typical enterprise service page: hero, explanation, offerings,
 * fit, process, stack, proof, FAQ, related services, contact.
 */
const ServiceDetail = ({ service: source }) => {
  const { translations } = useContext(LanguageContext)
  const service = localizeService(source, translations)
  const navigate = useNavigate()
  const animateIn = useEntryMotion()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [source.slug])

  const path = `/services/${service.slug}`
  const category = SERVICE_CATEGORIES.find((c) => c.id === service.category)
  const related = service.related.map(getService).filter(Boolean).map((item) => localizeService(item, translations))
  const caseStudy = service.caseStudy
    ? resolveProjects(translations).find((project) => project.title === service.caseStudy)
    : null

  const openProject = (link) => {
    if (link.startsWith('http')) {
      window.open(link, '_blank', 'noopener')
      return
    }
    navigate(link)
  }

  const hero = (delay) => ({
    initial: animateIn ? { opacity: 0, y: 40 } : false,
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  })

  return (
    <Layout>
      <Seo
        title={service.seoTitle}
        description={service.seoDescription}
        image={caseStudy ? caseStudy.img : undefined}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
            { name: service.name, path },
          ]),
          serviceDetailJsonLd({ name: service.name, description: service.seoDescription, path }),
          faqJsonLd(service.faq),
        ]}
      />

      <section className='svc-hero'>
        <div className='svc-inner'>
          <nav className='svc-breadcrumb' aria-label='Breadcrumb'>
            <Link to='/'>{translations.uiHome}</Link>
            <span aria-hidden='true'>/</span>
            <Link to='/services'>{translations.navServices}</Link>
            <span aria-hidden='true'>/</span>
            <span aria-current='page'>{service.name}</span>
          </nav>

          <div className='svc-hero-grid'>
            <div className='svc-hero-copy'>
              <motion.p className='svc-eyebrow' {...hero(0)}>{category ? (translations[`svcCat${category.id.charAt(0).toUpperCase()}${category.id.slice(1)}`] || category.label) : ''}</motion.p>
              <motion.h1 className='svc-hero-title svc-detail-title' {...hero(0.08)}>{service.title}</motion.h1>
              <motion.p className='svc-hero-tagline' {...hero(0.16)}>{service.tagline}</motion.p>
              <motion.p className='svc-hero-text' {...hero(0.24)}>{service.lead}</motion.p>

              <motion.div className='svc-actions' {...hero(0.32)}>
                <Link to='/contact-us' className='svc-btn svc-btn-primary'>{translations.svcHeroPrimary}</Link>
                {service.slug === 'seo' ? (
                  <Link to='/seo-check' className='svc-btn svc-btn-ghost'>
                    {translations.seocNavLabel}
                  </Link>
                ) : (
                  <Link
                    to={`/estimate?type=${ESTIMATOR_TYPE[service.slug] || ''}`}
                    className='svc-btn svc-btn-ghost'
                    onClick={() => track('estimator_click', { from: 'service', slug: service.slug })}
                  >
                    {translations.svcEstimateBtn}
                  </Link>
                )}
              </motion.div>
            </div>
            <motion.div className='svc-hero-visual-wrap' {...hero(0.2)}>
              {serviceCategoryPhotos[service.category] ? (
                <img className='svc-hero-visual svc-hero-photo' src={serviceCategoryPhotos[service.category]} alt='' />
              ) : (
                <AbstractVisual className='svc-hero-visual' shape={service.visual.shape} colors={service.visual.colors} />
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <section className='svc-section svc-statement'>
        <div className='svc-inner'>
          <Reveal>
            <h2 className='svc-display'>{service.statement}</h2>
          </Reveal>
          <div className='svc-statement-cols'>
            {service.intro.map((paragraph, index) => (
              <Reveal key={index} delay={0.1 * (index + 1)}><p>{paragraph}</p></Reveal>
            ))}
          </div>
        </div>
      </section>

      {service.deepDive && (
        <section className='svc-section svc-deep'>
          <div className='svc-inner'>
            <Reveal>
              <p className='svc-eyebrow'>{translations.svcDeepLabel || 'In depth'}</p>
              <h2 className='svc-display'>{service.deepDive.title}</h2>
            </Reveal>
            <div className='svc-deep-grid'>
              {service.deepDive.items.map((item, index) => (
                <Reveal key={item.title} delay={(index % 2) * 0.06} className='svc-deep-item'>
                  <h3 className='svc-deep-title'>
                    <span className='svc-deep-num'>{pad(index + 1)}</span>
                    {item.title}
                  </h3>
                  {item.text.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className='svc-section'>
        <div className='svc-inner'>
          <Reveal>
            <p className='svc-eyebrow'>{service.name}</p>
            <h2 className='svc-display'>{translations.svcOfferTitle}</h2>
          </Reveal>
          <div className='svc-grid svc-grid-3'>
            {service.offerings.map((offering, index) => (
              <Reveal key={offering.title} delay={(index % 3) * 0.06} className='svc-card-wrap'>
                <article className='svc-card'>
                  <p className='svc-card-meta'><span>{pad(index + 1)}</span></p>
                  <h3 className='svc-card-title'>{offering.title}</h3>
                  <p className='svc-card-text'>{offering.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className='svc-section'>
        <div className='svc-inner svc-fit'>
          <Reveal>
            <h2 className='svc-display'>{translations.svcFitTitle}</h2>
          </Reveal>
          <ul className='svc-fit-list'>
            {service.useCases.map((useCase, index) => (
              <li key={useCase}>
                <Reveal delay={(index % 2) * 0.08}>{useCase}</Reveal>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className='svc-section'>
        <div className='svc-inner'>
          <Reveal>
            <h2 className='svc-display'>{translations.svcDeliverHeading}</h2>
          </Reveal>
          <ol className='svc-stages svc-stages-detail'>
            {service.process.map((step, index) => (
              <li key={step.title} className='svc-stage'>
                <Reveal delay={index * 0.08}>
                  <span className='svc-stage-num'>{pad(index + 1)}</span>
                  <span className='svc-stage-name'>{step.title}</span>
                  <span className='svc-stage-text'>{step.text}</span>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className='svc-section'>
        <div className='svc-inner'>
          <Reveal>
            <p className='svc-eyebrow'>{translations.svcTechLabel}</p>
            <h2 className='svc-display svc-display-sm'>{(translations.svcStackTitle || 'Tools we use for {name}').replace('{name}', service.name)}</h2>
            <ul className='svc-stack'>
              {service.stack.map((tool) => <li key={tool}>{tool}</li>)}
            </ul>
          </Reveal>
        </div>
      </section>

      {caseStudy && (
        <section className='svc-section'>
          <div className='svc-inner'>
            <Reveal>
              <a
                className='svc-case'
                href={caseStudy.link}
                onClick={(e) => {
                  e.preventDefault()
                  openProject(caseStudy.link)
                }}
                {...(caseStudy.link.startsWith('http') ? { target: '_blank', rel: 'noopener' } : {})}
              >
                <div className='svc-work-img-frame'>
                  <ProjectMedia project={caseStudy} loading='lazy' width='720' height='480' />
                </div>
                <div className='svc-case-body'>
                  <p className='svc-eyebrow'>{translations.svcWorkEyebrow}</p>
                  <p className='svc-work-tag'>{caseStudy.tag}</p>
                  <h2 className='svc-case-title'>
                    {caseStudy.title} <span aria-hidden='true'>→</span>
                  </h2>
                  <p className='svc-case-text'>{caseStudy.text}</p>
                </div>
              </a>
            </Reveal>
          </div>
        </section>
      )}

      <section className='svc-section'>
        <div className='svc-inner svc-faq'>
          <Reveal>
            <h2 className='svc-display'>{translations.svcFaqTitle}</h2>
          </Reveal>
          <div className='svc-faq-list'>
            {service.faq.map((item, index) => (
              <details key={item.question} className='svc-faq-item' open={index === 0}>
                <summary>
                  <h3>{item.question}</h3>
                  <span className='svc-faq-icon' aria-hidden='true' />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className='svc-section svc-related-section'>
        <div className='svc-inner'>
          <Reveal>
            <h2 className='svc-display svc-display-sm'>{translations.svcRelatedTitle}</h2>
          </Reveal>
          <div className='svc-related'>
            {related.map((item, index) => (
              <Reveal key={item.slug} delay={index * 0.08} className='svc-related-wrap'>
                <Link to={`/services/${item.slug}`} className='svc-link-card'>
                  <h3 className='svc-card-title'>{item.name}</h3>
                  <p className='svc-card-text'>{item.summary}</p>
                  <span className='svc-link-more'>{translations.uiLearnMore} <span aria-hidden='true'>→</span></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <RelatedArticles ids={SERVICE_ARTICLES[source.slug]} />

      <section className='svc-section svc-cta'>
        <div className='svc-inner'>
          {/* The form is on the page: a visitor who arrived from search can write
              without first finding the contact page. */}
          <div className='svc-cta-grid'>
            <Reveal>
              <p className='svc-eyebrow svc-eyebrow-light'>{translations.svcCtaEyebrow}</p>
              <h2 className='svc-cta-title'>{translations.svcCtaTitle}</h2>
              <p className='svc-cta-text'>{translations.svcCtaText}</p>
              <Link to='/contact-us' className='svc-btn svc-btn-light'>{translations.svcCtaButton}</Link>
            </Reveal>
            <Reveal delay={0.1}>
              <QuoteForm
                source={`Service page: ${source.name}`}
                subject={`New project enquiry: ${source.name}`}
                topic={source.name}
                formName='service'
                need={NEED_INDEX[ESTIMATOR_TYPE[source.slug]]}
              />
            </Reveal>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default ServiceDetail
